import React, { useRef } from 'react';
import { Form } from '@dashboard/components/Formik';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePage from '@dashboard/hooks/usePage';
import PageMultiSelect from '@/dashboard/components/Formik/components/PageMultiSelect';
import getSubdomainCheckoutUrl from '@/dashboard/services/getSubdomainCheckoutUrl';
import getCustomDomainCheckoutUrl from '@/dashboard/services/getCustomDomainCheckoutUrl';
import { isPreview as isPreviewEnv } from '@dashboard/services/env';

const RedirectPageForm = () => {
  const { page, updatePage } = usePage();
  const { seller } = useAuth();
  const selectedPageIdRef = useRef(null);

  return (
    <Form
      initialValues={{
        redirect: {
          enabled: page?.redirect?.enabled || false,
          url: page?.redirect?.url || '',
        },
      }}
      fields={({ values }) => [
        {
          name: 'redirect.enabled',
          type: 'toggle',
          label: 'Redirect this page',
        },
        {
          type: 'custom',
          label: 'Redirect to another page',
          helpText: 'Select a checkout to redirect to or enter a URL below.',
          hidden: !values.redirect.enabled,
          renderField: (field, { setFieldValue }) => {
            const handlePageSelection = ({ pages }) => {
              if (!pages || pages.size === 0) {
                selectedPageIdRef.current = null;
                return;
              }

              const [key, value] = pages.entries().next().value ?? [];
              if (!key || !value) return;

              if (selectedPageIdRef.current !== key) {
                const subdomainCheckoutUrl = getSubdomainCheckoutUrl(value, seller);
                const customDomainCheckoutUrl =
                  !isPreviewEnv && getCustomDomainCheckoutUrl(value, seller);

                selectedPageIdRef.current = key;

                if (customDomainCheckoutUrl) {
                  setFieldValue('redirect.url', customDomainCheckoutUrl);
                } else {
                  setFieldValue('redirect.url', subdomainCheckoutUrl);
                }
              }
            };

            return (
              <PageMultiSelect
                name="redirect.page"
                isSingle
                onChange={handlePageSelection}
                placeholder="Search for page"
              />
            );
          },
        },
        {
          name: 'redirect.url',
          type: 'url',
          label: 'URL to redirect to',
          placeholder: 'https://yoursite.com',
          required: values.redirect.enabled,
          hidden: !values.redirect.enabled,
        },
      ]}
      onSubmit={async (values, actions) => {
        const { data, error } = await updatePage.mutateAsync(values);

        if (data) {
          actions.resetForm({
            values: {
              redirect: data.redirect,
            },
          });
        }

        return { data, error };
      }}
    />
  );
};

export default RedirectPageForm;
